import { Delete } from '@mui/icons-material'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material'
import {
  DataGridPro,
  GridActionsCellItem,
  GridRenderCellParams,
  GridRowsProp,
  GridValueFormatterParams
} from '@mui/x-data-grid-pro'
import { FC, useCallback, useContext, useMemo, useState } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { AppContext } from '../../../providers/AppProvider/AppProvider'
import { NotificationContext } from '../../../providers/NotificationProvider/NotificationProvider'
import {
  useArchiveRepairOrderAttachmentMutation,
  useCreateRepairOrderAttachmentEntryMutation
} from '../../../redux/api'
import { RepairOrderAttachmentEntry, UserProfile } from '../../../sdk'
import formatMdyDate from '../../../utils/formatMdyDate/formatMdyDate'
import { StyledDropzone } from '../../SharedUI/Dropzone/StyledDropzone'
import { DeleteRepairOrderAttachmentDialog } from './DeleteRepairOrderAttachmentDialog'

interface Props {
  open: boolean
  handleClose: () => void
  repairOrderId: string
  attachments: RepairOrderAttachmentEntry[]
  creators: UserProfile[]
}

const RepairOrderAttachmentDialog: FC<Props> = ({
  open,
  handleClose,
  repairOrderId,
  attachments,
  creators
}) => {
  const { overTabletBreakpoint } = useContext(AppContext)
  const { setNotice } = useContext(NotificationContext)

  const { accessToken } = useAccessTokenAndShopId()

  const [createRepairOrderAttachment, { isLoading }] =
    useCreateRepairOrderAttachmentEntryMutation()
  const [archiveRepairOrderAttachment] =
    useArchiveRepairOrderAttachmentMutation()

  const [files, setFiles] = useState<File[]>([])

  const handleSubmit = useCallback(async () => {
    if (files.length !== 1) {
      return
    }

    await createRepairOrderAttachment({
      authorization: accessToken,
      repairOrder: repairOrderId,
      file: files[0]
    })

    setNotice('Uploaded Successfully')
    handleClose()
  }, [
    accessToken,
    createRepairOrderAttachment,
    files,
    handleClose,
    repairOrderId,
    setNotice
  ])

  const [deleteId, setDeleteId] = useState<string | undefined>(undefined)

  const handleDelete = useCallback(async () => {
    await archiveRepairOrderAttachment({
      authorization: accessToken,
      attachmentId: deleteId!
    })

    setNotice('Deleted Successfully')
    setDeleteId(undefined)
    handleClose()
  }, [
    accessToken,
    archiveRepairOrderAttachment,
    deleteId,
    handleClose,
    setNotice
  ])

  const columns = useMemo(
    () => [
      {
        field: 'fileName',
        headerName: 'Attachments',
        flex: 2
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        flex: 1
      },
      {
        field: 'createdAt',
        headerName: 'Created On',
        flex: 1,
        valueFormatter: (params: GridValueFormatterParams) =>
          formatMdyDate(params.value as Date)
      },
      {
        field: 'actions',
        type: 'actions',
        getActions: (params: GridRenderCellParams) => {
          return [
            <GridActionsCellItem
              icon={<DownloadIcon />}
              label="Download"
              key="Download"
              onClick={() => {
                const attachmentUrl = attachments.find(
                  (entry) => entry.id === params.id
                )!.attachmentUrl!
                window.open(attachmentUrl, '_blank')
              }}
            />,
            <GridActionsCellItem
              icon={<Delete />}
              label="Delete"
              key="Delete"
              onClick={() => {
                setDeleteId(params.id as string)
              }}
            />
          ]
        }
      }
    ],
    [attachments]
  )

  const rows = useMemo(() => {
    const repairOrderAttachmentEntry: GridRowsProp = attachments.map(
      (entry: RepairOrderAttachmentEntry) => {
        const url = new URL(entry.attachmentUrl!)
        const pathSegments = url.pathname.split('/')
        const fileName = pathSegments[pathSegments.length - 1]

        return {
          id: entry.id,
          createdBy: creators?.find(
            (creator) => creator.id === entry.info?.createdBy
          )?.name,
          createdAt: entry.info?.createdAt,
          fileName
        }
      }
    )
    return repairOrderAttachmentEntry
  }, [attachments, creators])

  return (
    <>
      {deleteId && (
        <DeleteRepairOrderAttachmentDialog
          entry={attachments.find((entry) => entry.id === deleteId)!}
          handleClose={() => setDeleteId(undefined)}
          handleDelete={handleDelete}
        />
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={overTabletBreakpoint ? undefined : true}>
        <DialogTitle>Attachments</DialogTitle>
        <DialogContent>
          <DataGridPro
            rows={rows}
            columns={columns}
            autoHeight
            density="compact"
            sortingOrder={['asc', 'desc']}
            hideFooter={true}
            sx={{ mb: 3 }}
          />
          <StyledDropzone setFiles={setFiles} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          <Button
            variant={'text'}
            onClick={handleSubmit}
            disabled={files.length !== 1 || isLoading}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default RepairOrderAttachmentDialog
